import { useOfflineSync } from "@/contexts/OfflineSyncContext";
import { useTranslation } from "react-i18next";
import { CloudArrowUp, CloudSlash, ArrowsClockwise, CheckCircle } from "@phosphor-icons/react";

/**
 * Indicador de estado offline + cola de sincronización.
 * Se monta en el sidebar (Layout). Muestra conexión, pendientes y botón de sync.
 */
export default function OfflineIndicator() {
  const { t } = useTranslation();
  const sync = useOfflineSync();
  if (!sync) return null;
  const { online, pending, syncing, syncNow } = sync;

  // En línea y sin pendientes: indicador discreto.
  if (online && pending === 0) {
    return (
      <div data-testid="offline-indicator-ok"
           className="mx-3 mt-2 flex items-center gap-2 px-3 py-1.5 rounded-sm bg-zinc-900 border border-zinc-800">
        <CheckCircle size={14} weight="fill" className="text-emerald-500 shrink-0" />
        <span className="text-[10px] uppercase tracking-widest text-slate-400 truncate">{t("offline.online")}</span>
      </div>
    );
  }

  return (
    <div data-testid="offline-indicator"
         className={`mx-3 mt-2 rounded-sm border px-3 py-2 ${online ? "bg-amber-950/40 border-amber-700" : "bg-red-950/40 border-red-800"}`}>
      <div className="flex items-center gap-2">
        {online
          ? <CloudArrowUp size={16} weight="duotone" className="text-amber-400 shrink-0" />
          : <CloudSlash size={16} weight="duotone" className="text-red-400 shrink-0" />}
        <div className="min-w-0 flex-1">
          <p className="text-[9px] uppercase tracking-widest text-slate-400 leading-none mb-0.5">
            {online ? t("offline.pendingUpload") : t("offline.noConnection")}
          </p>
          <p className="text-xs font-bold text-white leading-none">
            {pending > 0 ? t("offline.inQueue", { count: pending }) : t("offline.offlineMode")}
          </p>
        </div>
      </div>
      {online && pending > 0 && (
        <button
          type="button"
          onClick={syncNow}
          disabled={syncing}
          data-testid="offline-sync-now"
          className="mt-2 w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-sm bg-[var(--brand,#E31E24)] text-white text-[11px] font-bold uppercase tracking-wider hover:opacity-90 disabled:opacity-50"
        >
          <ArrowsClockwise size={13} weight="bold" className={syncing ? "animate-spin" : ""} />
          {syncing ? t("offline.syncing") : t("offline.syncNow")}
        </button>
      )}
    </div>
  );
}
